'use client';

import { useState, useEffect, useCallback } from 'react';

interface CalendarEvent {
  id: string;
  summary: string;
  description?: string;
  location?: string;
  start: { dateTime?: string; date?: string };
  end: { dateTime?: string; date?: string };
  htmlLink: string;
  colorId?: string;
}

const MONTHS_FR = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
];
const MONTHS_SHORT_FR = [
  'Jan',
  'Fév',
  'Mar',
  'Avr',
  'Mai',
  'Jun',
  'Jul',
  'Aoû',
  'Sep',
  'Oct',
  'Nov',
  'Déc',
];
const DAYS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function getStartDate(event: CalendarEvent): Date {
  const raw = event.start.dateTime ?? (event.start.date ? event.start.date + 'T00:00:00' : null);
  return raw ? new Date(raw) : new Date();
}

function formatTime(event: CalendarEvent): string {
  if (event.start.dateTime) {
    const start = new Date(event.start.dateTime);
    const end = event.end.dateTime ? new Date(event.end.dateTime) : null;
    const startStr = start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    if (end) {
      const endStr = end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      return `${startStr} – ${endStr}`;
    }
    return startStr;
  }
  return 'Toute la journée';
}

function formatFullDate(event: CalendarEvent): string {
  const date = getStartDate(event);
  return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

export default function CalendrierPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-indexed
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<number | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/calendar?year=${year}&month=${month}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error ?? `Erreur ${res.status}`);
      }
      setEvents(data.events ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const prevMonth = () => {
    setSelectedDay(null);
    if (month === 1) {
      setMonth(12);
      setYear((y) => y - 1);
    } else setMonth((m) => m - 1);
  };

  const nextMonth = () => {
    setSelectedDay(null);
    if (month === 12) {
      setMonth(1);
      setYear((y) => y + 1);
    } else setMonth((m) => m + 1);
  };

  const goToToday = () => {
    setSelectedDay(today.getDate());
    setYear(today.getFullYear());
    setMonth(today.getMonth() + 1);
  };

  // --- Calendar grid ---
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayJS = new Date(year, month - 1, 1).getDay(); // 0=Sun
  const firstDayMon = firstDayJS === 0 ? 6 : firstDayJS - 1; // Mon=0 … Sun=6

  // Map: day number → events on that day
  const eventsByDay = new Map<number, CalendarEvent[]>();
  events.forEach((event) => {
    const dateStr = event.start.dateTime ? event.start.dateTime.split('T')[0] : event.start.date;
    if (dateStr) {
      const day = parseInt(dateStr.split('-')[2], 10);
      if (!eventsByDay.has(day)) eventsByDay.set(day, []);
      eventsByDay.get(day)!.push(event);
    }
  });

  const displayedEvents = selectedDay ? (eventsByDay.get(selectedDay) ?? []) : events;

  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth() + 1;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-r from-[#006C8F] to-[#005570] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block px-6 py-2 bg-[#FFC72C] text-[#006C8F] font-bold rounded-full mb-4 text-sm tracking-widest uppercase">
            Sporting Club Houlgate
          </div>
          <h1
            className="text-5xl md:text-6xl font-bold mb-4"
            style={{ fontFamily: 'var(--font-playfair), serif' }}
          >
            Calendrier
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
            Tournois, stages, compétitions et animations — retrouvez tous les événements du club.
          </p>

          {/* Boutons d'abonnement */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent('3609d604144bba51bea1b9e28133f7f3716de19f019b1fc1267496a1889a93ab@group.calendar.google.com')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-[#006C8F] font-semibold text-sm rounded-full shadow-sm hover:bg-[#FFC72C] hover:text-[#005570] transition-colors"
            >
              {/* Google Calendar icon */}
              <svg
                className="w-4 h-4 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="3"
                  y="4"
                  width="18"
                  height="17"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M16 2v4M8 2v4M3 9h18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path d="M8 14h2v2H8z" fill="currentColor" />
                <path d="M11 14h2v2h-2z" fill="currentColor" />
                <path d="M14 14h2v2h-2z" fill="currentColor" />
                <path d="M8 17h2v2H8z" fill="currentColor" />
                <path d="M11 17h2v2h-2z" fill="currentColor" />
              </svg>
              Ajouter à Google Calendar
            </a>

            <a
              href={`https://calendar.google.com/calendar/ical/${encodeURIComponent('3609d604144bba51bea1b9e28133f7f3716de19f019b1fc1267496a1889a93ab@group.calendar.google.com')}/public/basic.ics`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-white/15 text-white font-semibold text-sm rounded-full border border-white/30 hover:bg-white/25 transition-colors"
            >
              {/* ICS icon */}
              <svg
                className="w-4 h-4 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="3"
                  y="4"
                  width="18"
                  height="17"
                  rx="2"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M16 2v4M8 2v4M3 9h18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M12 13v4M10 15h4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Abonner (iCal / Outlook)
            </a>
          </div>
        </div>
      </section>

      {/* ── Main content ─────────────────────────────────────────── */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Month navigator */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={prevMonth}
              className="w-10 h-10 flex items-center justify-center rounded-full text-[#006C8F] hover:bg-[#006C8F]/10 transition-colors"
              aria-label="Mois précédent"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex flex-col items-center gap-1">
              <h2
                className="text-2xl md:text-3xl font-bold text-[#006C8F]"
                style={{ fontFamily: 'var(--font-playfair), serif' }}
              >
                {MONTHS_FR[month - 1]} {year}
              </h2>
              {!isCurrentMonth && (
                <button
                  onClick={goToToday}
                  className="text-xs text-[#006C8F]/60 hover:text-[#006C8F] underline underline-offset-2 transition-colors"
                >
                  Revenir à aujourd&apos;hui
                </button>
              )}
            </div>

            <button
              onClick={nextMonth}
              className="w-10 h-10 flex items-center justify-center rounded-full text-[#006C8F] hover:bg-[#006C8F]/10 transition-colors"
              aria-label="Mois suivant"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* ── Calendar grid ──────────────────────────────────────── */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-8">
            {/* Day-name header */}
            <div className="grid grid-cols-7 bg-[#006C8F]">
              {DAYS_FR.map((d) => (
                <div
                  key={d}
                  className="py-3 text-center text-xs font-semibold text-white tracking-widest uppercase"
                >
                  {d}
                </div>
              ))}
            </div>

            {/* Grid cells */}
            <div className="grid grid-cols-7 border-l border-t border-gray-100">
              {/* Leading empty cells */}
              {Array.from({ length: firstDayMon }).map((_, i) => (
                <div
                  key={`empty-${i}`}
                  className="h-14 border-b border-r border-gray-100 bg-gray-50/40"
                />
              ))}

              {/* Day cells */}
              {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                const dayEvents = eventsByDay.get(day) ?? [];
                const isToday = isCurrentMonth && day === today.getDate();
                const isSelected = day === selectedDay;
                const hasEvents = dayEvents.length > 0;

                return (
                  <button
                    key={day}
                    onClick={() =>
                      hasEvents ? setSelectedDay(isSelected ? null : day) : undefined
                    }
                    disabled={!hasEvents}
                    className={[
                      'h-14 border-b border-r border-gray-100 flex flex-col items-center justify-start pt-1.5 gap-0.5 transition-colors',
                      isSelected
                        ? 'bg-[#006C8F]'
                        : hasEvents
                          ? 'hover:bg-[#006C8F]/5 cursor-pointer'
                          : 'cursor-default',
                    ].join(' ')}
                  >
                    <span
                      className={[
                        'w-7 h-7 flex items-center justify-center rounded-full text-sm font-medium transition-colors',
                        isSelected
                          ? 'text-white'
                          : isToday
                            ? 'bg-[#FFC72C] text-[#006C8F] font-bold'
                            : hasEvents
                              ? 'text-[#006C8F] font-semibold'
                              : 'text-gray-400',
                      ].join(' ')}
                    >
                      {day}
                    </span>
                    {hasEvents && (
                      <div className="flex gap-0.5">
                        {dayEvents.slice(0, 3).map((_, ei) => (
                          <span
                            key={ei}
                            className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-white/80' : 'bg-[#FFC72C]'}`}
                          />
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Legend ─────────────────────────────────────────────── */}
          <div className="flex items-center gap-4 mb-6 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-[#FFC72C] flex items-center justify-center text-[#006C8F] font-bold text-[10px]">
                {today.getDate()}
              </span>
              Aujourd&apos;hui
            </span>
            <span className="flex items-center gap-1.5">
              <span className="flex gap-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFC72C]" />
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFC72C]" />
              </span>
              Événements
            </span>
          </div>

          {/* ── Events list ────────────────────────────────────────── */}
          {loading ? (
            <div className="flex flex-col items-center py-16 gap-3 text-[#006C8F]/60">
              <svg className="w-8 h-8 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4l3-3-3-3v4A10 10 0 002 12h2z"
                />
              </svg>
              <span className="text-sm">Chargement des événements…</span>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center text-red-600 text-sm">
              {error}
            </div>
          ) : (
            <>
              {/* Section heading */}
              <div className="flex items-center justify-between mb-4">
                <h3
                  className="text-lg font-bold text-gray-800"
                  style={{ fontFamily: 'var(--font-playfair), serif' }}
                >
                  {selectedDay
                    ? `${selectedDay} ${MONTHS_FR[month - 1]}`
                    : displayedEvents.length > 0
                      ? `Événements de ${MONTHS_FR[month - 1]}`
                      : null}
                </h3>
                {selectedDay && (
                  <button
                    onClick={() => setSelectedDay(null)}
                    className="text-xs text-[#006C8F]/60 hover:text-[#006C8F] transition-colors flex items-center gap-1"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Tous les événements
                  </button>
                )}
              </div>

              {displayedEvents.length === 0 ? (
                <div className="text-center py-14">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg
                      className="w-8 h-8 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.5}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 9v7.5"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Aucun événement prévu{selectedDay ? ' ce jour' : ' ce mois-ci'}.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {displayedEvents.map((event) => {
                    const startDate = getStartDate(event);
                    const dayNum = startDate.getDate();
                    const monthAbbr = MONTHS_SHORT_FR[startDate.getMonth()];
                    const isAllDay = !event.start.dateTime;

                    return (
                      <div
                        key={event.id}
                        className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md hover:border-[#006C8F]/20 transition-all"
                      >
                        <div className="flex items-start gap-4">
                          {/* Date badge */}
                          <div className="flex-shrink-0 w-12 h-12 bg-[#006C8F]/8 rounded-xl flex flex-col items-center justify-center border border-[#006C8F]/15">
                            <span className="text-base font-bold text-[#006C8F] leading-none">
                              {dayNum}
                            </span>
                            <span className="text-[10px] text-[#006C8F]/60 font-medium uppercase tracking-wide leading-none mt-0.5">
                              {monthAbbr}
                            </span>
                          </div>

                          {/* Event info */}
                          <div className="flex-1 min-w-0">
                            <h4
                              className="font-semibold text-gray-900 text-sm leading-snug mb-1 truncate"
                              style={{ fontFamily: 'var(--font-playfair), serif' }}
                            >
                              {event.summary}
                            </h4>

                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                              {/* Time */}
                              <span className="flex items-center gap-1">
                                <svg
                                  className="w-3.5 h-3.5 text-[#FFC72C]"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth={2}
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z"
                                  />
                                </svg>
                                {isAllDay ? (
                                  <span className="inline-block px-1.5 py-0.5 bg-[#FFC72C]/20 text-[#006C8F] rounded text-[10px] font-semibold">
                                    Toute la journée
                                  </span>
                                ) : (
                                  formatTime(event)
                                )}
                              </span>

                              {/* Location */}
                              {event.location && (
                                <span className="flex items-center gap-1 truncate max-w-xs">
                                  <svg
                                    className="w-3.5 h-3.5 text-[#FFC72C] flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth={2}
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                                    />
                                  </svg>
                                  {event.location}
                                </span>
                              )}
                            </div>

                            {/* Description */}
                            {event.description && (
                              <p className="mt-1.5 text-xs text-gray-400 line-clamp-2 leading-relaxed">
                                {event.description.replace(/<[^>]*>/g, '')}
                              </p>
                            )}
                          </div>

                          {/* Full date (hidden on small screens) */}
                          <div className="hidden sm:flex flex-col items-end gap-1 flex-shrink-0 text-right">
                            <span className="text-xs text-gray-400 capitalize">
                              {formatFullDate(event)}
                            </span>
                            {event.htmlLink && (
                              <a
                                href={event.htmlLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] text-[#006C8F] hover:text-[#005570] underline underline-offset-2 transition-colors"
                              >
                                Voir dans Google&nbsp;Calendar
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
